import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Tab,
  Tabs,
  Stack,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { UseFormReturn } from "react-hook-form";
import { ReactComponent as ModifyIcon } from "../../../../../../../../assets/icons/MedintModifyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../../../assets/icons/MedintDeleteIcon.svg";
import { RHFTextField } from "../../../../../../../RHFComponents/RHFComponents";
import { StyledDataGrid } from "../../../../../../../AppointmentManagement/AppointmentManagementDetail/AppointmentManagementDetail";
import SospensioniAgendeModalDetails from "./SospensioniAgendeModalDetails";
import { ReactComponent as AddIcon } from "../../../../../../../../assets/icons/MedintAddIcon.svg";
import { pgmtgps, ricPgmtgps } from "../../../../../../../../networking";
import { fmtDate } from "../../../../MedintBox/MedintBox";
import { AllertMessage } from "../MedintAgendaFormDetail";
import { ModalState, Entity, Action } from "../../../../../../../../services/medintAgendaFormTypes";

type Props = {
  open: boolean;
  onClose: () => void;
  parentMethods: UseFormReturn<any>;
  loading?: boolean;
  createdIdPgmedt?: string;
};

export type PgmtgpsData = {
  DECODE_TPGPRE_TPGPRE_DESCR: string;
  PGMEDT_PRGINT: string;
  PGMTGPS_DATA_SOSPENSIONE: string;
  PGMTGPS_NOTA_AGENDA: string;
  PGMTGPS_NOTE_SOSPENSIONE: string;
  PGMTGPS_PID_ACU: string;
  PGMTGPS_PRGINT: string;
  PGMTGPS_SW_ALTRI_ORARI: string;
  PGMTGPS_SW_SOSPESA: string;
  PGMTGPS_TIMESTAMP: string;
  TPGPRE_CODICE: string;
};

const SospensioniAgendeModal = ({
  open,
  onClose,
  parentMethods,
  createdIdPgmedt,
}: Props) => {
  const { control } = parentMethods;

  //Stato per la gestione del modale
  const [isOpenModalDetails, setIsOpenModalDetails] = useState<ModalState>({
    open: false,
  });

  const [dataPgmtgps, setDataPgmtgps] = useState<any>([]);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] = useState<any>(null);

  const [loadingLocal, setLoadingLocal] = useState<boolean>(false);

  const idPgmedt =
    parentMethods.getValues("medIntNuovoProgramma.chiave") ||
    (createdIdPgmedt as string | undefined);

  // Stili
  const BLUE = "#003267";

  const sxTabs = {
    mb: 2,
    minHeight: 38,
    "& .MuiTabs-indicator": {
      backgroundColor: BLUE,
      height: 3,
      borderRadius: 3,
    },
    "& .MuiTab-root": {
      minHeight: 38,
      textTransform: "none",
      px: 0,
      mr: 3,
      fontFamily: "Montserrat",
      fontWeight: "bold",
      color: "#9e9e9e",
      transition: "color .2s ease",
    },
    "& .MuiTab-root.Mui-selected": { color: BLUE },
  } as const;

  const openModal = (entity: Entity, action: Action, payload?: any) =>
    setIsOpenModalDetails({ open: true, entity, action, payload });

  const closeModal = () => setIsOpenModalDetails({ open: false });

  //Funzione per mappare i dati in ingresso dell'api da mostrare nello StyledDataGrid
  const sospensioniRows = useMemo(() => {
    const arr = (dataPgmtgps ?? []) as PgmtgpsData[];

    return arr.map((x) => ({
      id: x.PGMTGPS_PRGINT,
      codTipologia: x.TPGPRE_CODICE ?? "",
      descrTipologia: x.DECODE_TPGPRE_TPGPRE_DESCR ?? "",
      sospesa: x.PGMTGPS_SW_SOSPESA === "1" ? "S" : "N",
      dataSospensione: fmtDate(x.PGMTGPS_DATA_SOSPENSIONE) ?? "",
      _raw: x,
    }));
  }, [dataPgmtgps]);

  const columns: GridColDef[] = [
    {
      field: "codTipologia",
      headerName: "Cod. Tipologia",
      flex: 1,
      minWidth: 140,
      sortable: false,
    },
    {
      field: "descrTipologia",
      headerName: "Descr. Tipologia",
      flex: 2,
      minWidth: 220,
      sortable: false,
    },
    {
      field: "sospesa",
      headerName: "Sospesa",
      flex: 1,
      minWidth: 120,
      align: "left",
      headerAlign: "left",
      sortable: false,
    },
    {
      field: "dataSospensione",
      headerName: "Data Sospensione",
      flex: 1.2,
      minWidth: 160,
      sortable: false,
    },
    {
      field: "azioni",
      headerName: "Azioni",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <IconButton
            size="small"
            aria-label="Modifica"
            sx={{ color: "#024E88" }}
            onClick={() => {
              console.log(params.row._raw);
              openModal("sospensione", "edit", params.row._raw);
            }}
          >
            <ModifyIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Cancellazione"
            onClick={() => {
              console.log(params.row._raw.PGMTGPS_PRGINT);
              askConfirmDelete(params.row._raw.PGMTGPS_PRGINT);
            }}
            sx={{ color: "#d32f2f" }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const getPgmtgpsData = async () => {
    setLoadingLocal(true);
    try {
      const response = await ricPgmtgps({
        pgmedt_prgint: idPgmedt ?? createdIdPgmedt ?? "",
      });

      console.log("response", response);

      const data = response.data.dati.pgmtgps as PgmtgpsData[];
      setDataPgmtgps(data);
      console.log({ data });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingLocal(false);
    }
  };

  useEffect(() => {
    if ((!createdIdPgmedt && !idPgmedt) || !idPgmedt) {
      return;
    }

    getPgmtgpsData();
  }, [idPgmedt, createdIdPgmedt]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmDelete = (row: any) => {
    setPendingSubmit(row);
    setMessage("Confermare la cancellazione della sospensione?");
    setSeverity("warning");
    setShowAllert(true);
  };

  const handleDelete = async (row: any) => {
    if (!pendingSubmit) return;
    console.log(row);
    setLoadingLocal(true);
    try {
      const response = await pgmtgps({
        crud: "d",
        prgint: row,
      });
      console.log(response);
      if (response?.data?.esito === "OK") {
        setLoadingLocal(false);
        setSeverity("success");
        setMessage("Operazione completata con successo.");
      } else {
        setSeverity("error");
        setMessage("Salvataggio non riuscito.");
        return;
      }
      await getPgmtgpsData();
      setTimeout(() => onClose(), 800);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingLocal(false);
    }
  };

  return (
    <>
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={handleDelete}
      />
      {loadingLocal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      {isOpenModalDetails.open && isOpenModalDetails.entity && (
        <SospensioniAgendeModalDetails
          open={isOpenModalDetails.open}
          onClose={closeModal}
          parentMethods={parentMethods}
          initialData={
            isOpenModalDetails.action === "insert"
              ? undefined
              : isOpenModalDetails.payload
          }
          createdIdPgmedt={createdIdPgmedt}
          action={isOpenModalDetails.action}
          onSaved={getPgmtgpsData}
        />
      )}
      <Modal
        open={open}
        onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          onClose();
        }}
      >
        <Box
          sx={{
            width: "92vw",
            maxWidth: 980,
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            p: 2,
          }}
        >
          <Tabs
            value={0}
            sx={{ px: 1, ...sxTabs }}
            TabIndicatorProps={{ sx: { height: 3 } }}
          >
            <Tab
              label="Sospensioni Agende"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                textTransform: "none",
              }}
            />
          </Tabs>

          <Box sx={{ px: 1, pt: 2, pb: 2, maxWidth: 420 }}>
            <RHFTextField
              name="medIntNuovoProgramma.codProg"
              control={control}
              label="Programma"
              fullWidth
              disabled
            />
          </Box>

          <Box
            sx={{
              mx: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box sx={{ bgcolor: "background.paper" }}>
              <Tabs
                value={0}
                sx={{
                  px: 1,
                  "& .MuiTabs-indicator": {
                    backgroundColor: `${BLUE} !important`,
                  },
                }}
                TabIndicatorProps={{ sx: { height: 3 } }}
              >
                <Tab
                  label="Dettaglio Tipologie"
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 700,
                    textTransform: "none",
                    color: `${BLUE} !important`,
                  }}
                />
              </Tabs>
            </Box>
            <Box
              sx={{
                px: 2,
                pt: 1.5,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="outlinedCustom"
                startIcon={<AddIcon />}
                sx={{ textTransform: "none" }}
                onClick={() => openModal("sospensione", "insert")}
              >
                Inserisci
              </Button>
            </Box>
            <Box sx={{ p: 2, pt: 1.5 }}>
              <StyledDataGrid
                autoHeight
                rows={sospensioniRows ?? []}
                getRowId={(r) => r.id}
                columns={columns}
                loading={loadingLocal}
                hideFooter
                disableSelectionOnClick
                sortingOrder={["desc", "asc", null]}
              />
            </Box>
          </Box>
          <Box sx={{ mt: 2, px: 1 }}>
            <Button variant="outlinedCustom" onClick={onClose}>
              Indietro
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SospensioniAgendeModal;
